import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Clock, 
  Search,
  Filter,
  ChevronUp,
  ChevronDown,
  User,
  Truck,
  Shield,
  AlertTriangle,
  CheckCircle,
  Download,
  Eye,
  CheckSquare,
  Square,
  Upload,
  X,
  RotateCcw,
  FileCheck,
  Plus,
  Calendar,
  TrendingUp,
  AlertCircle,
  MoreHorizontal,
  Grid,
  List,
  SortAsc,
  SortDesc,
  Tag
} from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';
import documentStorageService from '../../services/documentStorageService';
import ImprovedUploadModal from './ImprovedUploadModal';
import MobileDocumentCard from './MobileDocumentCard';
import MobileSearchFilter from './MobileSearchFilter';
import MobileDocumentHeader from './MobileDocumentHeader';
import MobileUploadModal from './MobileUploadModal';

// Desktop Details Button Component  
const DesktopDetailsButton = ({ document, onToggle, isExpanded }) => {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
        isExpanded 
          ? 'bg-blue-700 text-white' 
          : 'bg-blue-600 text-white hover:bg-blue-700'
      }`}
      title="Show Document Details"
    >
      <FileCheck className="h-3 w-3" />
      <span>Details</span>
      <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}>
        <ChevronDown className="h-3 w-3" />
      </div>
    </button>
  );
};

// Row Details Component
const RowDetails = ({ document }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return '—';
    }
  };

  const formatCountdown = (daysLeft) => {
    if (typeof daysLeft !== 'number' || Number.isNaN(daysLeft)) {
      return 'No expiration';
    }
    if (daysLeft < 0) {
      return `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? '' : 's'} ago`;
    }
    if (daysLeft === 0) return 'Expires today';
    if (daysLeft === 1) return 'Expires tomorrow';
    if (daysLeft <= 7) return `${daysLeft} days left`;
    if (daysLeft <= 30) return `${daysLeft} days left`;
    return `${Math.ceil(daysLeft / 30)} months left`;
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'driver':
        return User;
      case 'vehicle':
        return Truck;
      case 'company':
        return Shield;
      default:
        return FileText;
    }
  };

  const EntityIcon = getEntityIcon(document.entityType);

  return (
    <div className="bg-blue-50 border-t border-blue-200 px-6 py-4">
      {/* Header - Applies To */}
      <div className="mb-4 pb-3 border-b border-blue-200">
        <div className="flex items-center space-x-2 mb-2">
          <EntityIcon className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-semibold text-blue-900">Applies To</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-bold text-gray-900">{document.entityName}</p>
            <div className="flex items-center space-x-2 mt-1">
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                {document.entityType}
              </span>
              <span className="text-sm text-gray-600 capitalize">{document.category}</span>
            </div>
          </div>
          {document.entityId && (
            <div className="text-right">
              <span className="text-xs text-gray-500">Entity ID</span>
              <p className="text-sm font-medium text-gray-900">{document.entityId}</p>
            </div>
          )}
        </div>
      </div>

      {/* Grid Layout for Details */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 text-sm">
        {/* Document Type */}
        <div>
          <span className="text-xs text-gray-500 uppercase tracking-wide">Type</span>
          <p className="font-medium text-gray-900 capitalize mt-1">
            {document.type.replace(/_/g, ' ')}
          </p>
        </div>

        {/* Document Number */}
        {document.documentNumber && (
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">Number</span>
            <p className="font-medium text-gray-900 mt-1">{document.documentNumber}</p>
          </div>
        )}

        {/* Issue Date */}
        {document.issueDate && (
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">Issued</span>
            <p className="font-medium text-gray-900 mt-1">{formatDate(document.issueDate)}</p>
          </div>
        )}

        {/* Expiration Date */}
        {document.expirationDate && (
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">Expires</span>
            <p className={`font-medium mt-1 ${
              typeof document.daysLeft === 'number'
                ? (document.daysLeft < 0
                  ? 'text-red-600'
                  : document.daysLeft <= 30
                    ? 'text-amber-600'
                    : 'text-green-600')
                : 'text-gray-900'
            }`}>
              {formatDate(document.expirationDate)}
            </p>
          </div>
        )}

        {/* Status */}
        {typeof document.daysLeft === 'number' && (
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">Status</span>
            <div className="flex items-center space-x-1 mt-1">
              <Clock className={`h-3 w-3 ${
                document.daysLeft < 0 ? 'text-red-500' :
                document.daysLeft <= 30 ? 'text-amber-500' : 'text-green-500'
              }`} />
              <span className={`text-sm font-medium ${
                document.daysLeft < 0 ? 'text-red-600' :
                document.daysLeft <= 30 ? 'text-amber-600' : 'text-green-600'
              }`}>
                {formatCountdown(document.daysLeft)}
              </span>
            </div>
          </div>
        )}

        {/* Issuing Authority */}
        {document.issuingAuthority && (
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">Issued By</span>
            <p className="font-medium text-gray-900 mt-1">{document.issuingAuthority}</p>
          </div>
        )}

        {/* File Name */}
        {document.fileName && (
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">File</span>
            <p className="font-medium text-gray-900 mt-1 truncate" title={document.fileName}>
              {document.fileName}
            </p>
          </div>
        )}

        {/* File Size */}
        {document.fileSize && (
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">Size</span>
            <p className="font-medium text-gray-900 mt-1">
              {document.fileSize > 1024 * 1024 
                ? `${Math.round(document.fileSize / (1024 * 1024))} MB`
                : `${Math.round(document.fileSize / 1024)} KB`
              }
            </p>
          </div>
        )}

        {/* File Type */}
        {document.mimeType && (
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">Format</span>
            <p className="font-medium text-gray-900 mt-1">
              {document.mimeType.split('/')[1]?.toUpperCase() || 'Unknown'}
            </p>
          </div>
        )}

        {/* Uploaded By */}
        <div>
          <span className="text-xs text-gray-500 uppercase tracking-wide">Uploaded By</span>
          <p className="font-medium text-gray-900 mt-1">{document.uploadedBy}</p>
        </div>

        {/* Upload Date */}
        {document.uploadDate && (
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">Upload Date</span>
            <p className="font-medium text-gray-900 mt-1">{formatDate(document.uploadDate)}</p>
          </div>
        )}

        {/* Tags */}
        {document.tags && document.tags.length > 0 && (
          <div className="col-span-2">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Tags</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {document.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const STATUS_STYLES = {
  expired: {
    urgency: 'critical',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: AlertTriangle
  },
  expiring_soon: {
    urgency: 'high',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: Clock
  },
  pending_renewal: {
    urgency: 'high',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Clock
  },
  valid: {
    urgency: 'low',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle
  },
  active: {
    urgency: 'low',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle
  },
  archived: {
    urgency: 'low',
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    icon: FileText
  }
};

const DocumentListImproved = ({ 
  documents = [], 
  onDocumentAction,
  onBulkAction,
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    entityType: 'all',
    type: 'all',
    status: 'all',
    expiration: 'all',
    appliesTo: 'all'
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'expirationDate',
    direction: 'asc'
  });
  const [selectedDocuments, setSelectedDocuments] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showQuickUpload, setShowQuickUpload] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [selectedDocumentForVersion, setSelectedDocumentForVersion] = useState(null);
  const quickUploadRef = useRef(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const location = useLocation();
  const navigate = useNavigate();

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [remoteDocuments, setRemoteDocuments] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState(null);

  const hasExternalDocuments = Array.isArray(documents) && documents.length > 0;

  // Handle URL parameters for filtering
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const filterParam = searchParams.get('filter');
    
    if (filterParam) {
      switch (filterParam) {
        case 'expiring':
          setFilters(prev => ({ ...prev, status: 'expiring_soon' }));
          setShowFilters(true);
          break;
        case 'expired':
          setFilters(prev => ({ ...prev, status: 'expired' }));
          setShowFilters(true);
          break;
        default:
          break;
      }
    }
  }, [location.search]);

  // Data transformation utilities
  const toDateValue = useCallback((value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof value.toDate === 'function') {
      try {
        const parsed = value.toDate();
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      } catch (error) {
        return null;
      }
    }
    if (typeof value === 'object') {
      const seconds = typeof value.seconds === 'number' ? value.seconds : value._seconds;
      const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : value._nanoseconds;
      if (typeof seconds === 'number') {
        const millis = (seconds * 1000) + Math.floor((nanos || 0) / 1_000_000);
        const parsed = new Date(millis);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
    }
    return null;
  }, []);

  const toIsoString = useCallback((value) => {
    const date = toDateValue(value);
    return date ? date.toISOString() : null;
  }, [toDateValue]);

  const deriveEntityDetails = useCallback((doc = {}) => {
    const entityType = doc.entityType || (doc.driverId ? 'driver' : doc.vehicleId ? 'vehicle' : 'company');
    const entityId = doc.entityId || doc.driverId || doc.vehicleId || doc.companyId || null;
    const raw = doc.raw || {};
    const driver = raw.driver || raw.driverProfile || raw.driverInfo || {};
    const vehicle = raw.vehicle || raw.vehicleProfile || raw.vehicleInfo || {};
    const company = raw.company || raw.companyProfile || {};

    const possibleNames = [];
    const addName = (value) => {
      if (typeof value === 'string' && value.trim().length > 0) {
        possibleNames.push(value.trim());
      }
    };

    [
      doc.entityName, doc.driverName, doc.driverDisplayName, doc.driverFullName,
      doc.vehicleName, doc.vehicleNumber, doc.vehicleDisplayName, doc.companyName,
      raw.entityName, raw.associatedName, raw.associatedEntityName,
      driver.name, driver.fullName, driver.displayName, driver.preferredName,
      `${driver.firstName || ''} ${driver.lastName || ''}`,
      vehicle.name, vehicle.displayName, vehicle.unitNumber, vehicle.vehicleNumber, vehicle.licensePlate,
      company.name, company.legalName, company.displayName
    ].forEach(addName);

    const entityName = possibleNames.find(Boolean) || 
      (entityType === 'driver' && driver && (driver.firstName || driver.lastName)
        ? `${driver.firstName || ''} ${driver.lastName || ''}`.trim() : null) ||
      (entityType === 'vehicle' && vehicle && vehicle.unitNumber ? `Vehicle ${vehicle.unitNumber}` : null) ||
      (entityType === 'company' && company && company.legalName ? company.legalName : null) ||
      (entityType === 'driver' && entityId ? `Driver ${entityId}` :
        entityType === 'vehicle' && entityId ? `Vehicle ${entityId}` :
          entityType === 'company' && entityId ? `Company ${entityId}` : 'Document Record');

    return { entityType, entityId, entityName };
  }, []);

  const transformDocument = useCallback((doc) => {
    if (!doc || typeof doc !== 'object') return null;

    const expirationDate = toIsoString(doc.expirationDate);
    const issueDate = toIsoString(doc.issueDate);
    const uploadDate = toIsoString(doc.uploadDate || doc.createdAt || doc.updatedAt);

    const explicitDays = typeof doc.daysUntilExpiration === 'number' ? doc.daysUntilExpiration : null;
    const computedDays = expirationDate ? differenceInDays(parseISO(expirationDate), new Date()) : null;
    const daysUntilExpiration = Number.isFinite(explicitDays) ? explicitDays : computedDays;
    const { entityType, entityId, entityName } = deriveEntityDetails(doc);

    const title = doc.title || doc.documentTitle || doc.originalFileName || doc.documentType || 'Document';
    const documentType = (doc.documentType || doc.type || 'general').toLowerCase();
    const category = doc.category || 'general';
    const status = (doc.status || doc.documentStatus || 'active').toLowerCase();

    return {
      id: doc.id || doc.documentId || doc.uid,
      title, type: documentType, category, entityType, entityId, entityName,
      expirationDate, issueDate, uploadDate,
      documentNumber: doc.documentNumber || doc.referenceNumber || '',
      issuingAuthority: doc.issuingAuthority || doc.issuedBy || '',
      status,
      uploadedBy: doc.uploadedByName || doc.uploadedBy || doc.uploader?.name || 'System',
      fileName: doc.originalFileName || doc.fileName || '',
      mimeType: doc.mimeType || doc.contentType || '',
      fileSize: doc.fileSize || doc.size || null,
      fileUrl: doc.fileUrl || doc.downloadUrl || null,
      tags: Array.isArray(doc.tags) ? doc.tags : [],
      daysUntilExpiration,
      raw: doc
    };
  }, [deriveEntityDetails, toIsoString]);

  const fetchDocuments = useCallback(async () => {
    if (hasExternalDocuments) return;
    setIsLoadingData(true);
    setDataError(null);
    try {
      const response = await documentStorageService.getDocuments({ limit: 100, offset: 0 });
      const docs = Array.isArray(response?.documents) ? response.documents : [];
      const normalized = docs.map(transformDocument).filter(Boolean);
      setRemoteDocuments(normalized);
      setSelectedDocuments(new Set());
    } catch (error) {
      console.error('Failed to load documents', error);
      setDataError(error?.message || 'Failed to load documents.');
    } finally {
      setIsLoadingData(false);
    }
  }, [hasExternalDocuments, transformDocument]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const normalizedExternalDocuments = useMemo(() => {
    if (!hasExternalDocuments) return [];
    return documents.map(transformDocument).filter(Boolean);
  }, [hasExternalDocuments, documents, transformDocument]);

  const documentData = hasExternalDocuments ? normalizedExternalDocuments : remoteDocuments;

  const resolveStatusStyle = useCallback((statusKey) => {
    const normalized = statusKey && STATUS_STYLES[statusKey] ? statusKey :
      statusKey === 'pending_renewal' ? 'pending_renewal' :
        statusKey === 'expiring_soon' ? 'expiring_soon' :
          statusKey === 'expired' ? 'expired' :
            statusKey === 'archived' ? 'archived' :
              statusKey === 'active' ? 'active' : 'valid';
    const style = STATUS_STYLES[normalized] || STATUS_STYLES.valid;
    return { status: normalized, ...style };
  }, []);

  // Get document status with calculated expiration info
  const getDocumentStatus = useCallback((doc) => {
    if (!doc) return { ...resolveStatusStyle('valid'), daysLeft: null };

    const now = new Date();
    const expirationDate = toDateValue(doc.expirationDate);
    let daysLeft = Number.isFinite(doc.daysUntilExpiration) ? doc.daysUntilExpiration : null;

    if (expirationDate) {
      const diff = differenceInDays(expirationDate, now);
      if (Number.isFinite(diff)) daysLeft = diff;
    }

    if (typeof daysLeft === 'number') {
      if (daysLeft < 0) return { ...resolveStatusStyle('expired'), daysLeft };
      if (daysLeft <= 30) return { ...resolveStatusStyle('expiring_soon'), daysLeft };
      if (daysLeft <= 60) return { ...resolveStatusStyle('pending_renewal'), daysLeft };
      return { ...resolveStatusStyle('valid'), daysLeft };
    }

    const normalizedStatus = (doc.status || 'valid').toLowerCase();
    return { ...resolveStatusStyle(normalizedStatus), daysLeft };
  }, [resolveStatusStyle, toDateValue]);

  // Process documents with status calculations
  const processedDocuments = useMemo(() => {
    return (documentData || []).map((doc) => ({
      ...doc,
      ...getDocumentStatus(doc)
    }));
  }, [documentData, getDocumentStatus]);

  // Calculate document insights
  const documentInsights = useMemo(() => {
    const total = processedDocuments.length;
    const expired = processedDocuments.filter(doc => doc.status === 'expired').length;
    const expiringSoon = processedDocuments.filter(doc => doc.status === 'expiring_soon').length;
    const pendingRenewal = processedDocuments.filter(doc => doc.status === 'pending_renewal').length;
    const upcomingExpirations = processedDocuments
      .filter(doc => doc.daysLeft && doc.daysLeft > 0 && doc.daysLeft <= 30)
      .sort((a, b) => a.daysLeft - b.daysLeft)
      .slice(0, 5);

    return {
      total,
      expired,
      expiringSoon,
      pendingRenewal,
      needsAttention: expired + expiringSoon,
      upcomingExpirations,
      healthScore: total > 0 ? Math.round(((total - expired - expiringSoon) / total) * 100) : 100
    };
  }, [processedDocuments]);

  // Filter and search documents
  const filteredDocuments = useMemo(() => {
    let filtered = processedDocuments;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((doc) => {
        const fields = [doc.title, doc.entityName, doc.documentNumber, doc.type, doc.issuingAuthority];
        return fields.some((value) => typeof value === 'string' && value.toLowerCase().includes(searchLower));
      });
    }

    if (filters.entityType !== 'all') filtered = filtered.filter(doc => doc.entityType === filters.entityType);
    if (filters.type !== 'all') filtered = filtered.filter(doc => doc.type === filters.type);
    if (filters.appliesTo !== 'all') filtered = filtered.filter(doc => doc.entityName === filters.appliesTo);
    if (filters.status !== 'all') filtered = filtered.filter(doc => doc.status === filters.status);

    if (filters.expiration !== 'all') {
      filtered = filtered.filter(doc => {
        const daysLeft = typeof doc.daysLeft === 'number' ? doc.daysLeft : null;
        switch (filters.expiration) {
          case 'expired': return daysLeft !== null && daysLeft < 0;
          case 'expiring_soon': return daysLeft !== null && daysLeft >= 0 && daysLeft <= 30;
          case 'expiring_later': return daysLeft !== null && daysLeft > 30 && daysLeft <= 90;
          case 'valid': return daysLeft !== null && daysLeft > 90;
          default: return true;
        }
      });
    }

    return filtered;
  }, [processedDocuments, searchTerm, filters]);

  // Sort documents
  const sortedDocuments = useMemo(() => {
    const sorted = [...filteredDocuments];
    sorted.sort((a, b) => {
      let aValue, bValue;
      switch (sortConfig.field) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'entityName':
          aValue = a.entityName.toLowerCase();
          bValue = b.entityName.toLowerCase();
          break;
        case 'type':
          aValue = a.type.toLowerCase();
          bValue = b.type.toLowerCase();
          break;
        case 'expirationDate':
          aValue = toDateValue(a.expirationDate) || new Date(8640000000000000);
          bValue = toDateValue(b.expirationDate) || new Date(8640000000000000);
          break;
        case 'status':
          const statusOrder = { expired: 0, expiring_soon: 1, pending_renewal: 2, valid: 3, active: 3, archived: 4 };
          aValue = statusOrder[a.status] ?? 5;
          bValue = statusOrder[b.status] ?? 5;
          break;
        case 'uploadDate':
          aValue = toDateValue(a.uploadDate) || new Date(0);
          bValue = toDateValue(b.uploadDate) || new Date(0);
          break;
        default:
          aValue = a[sortConfig.field];
          bValue = b[sortConfig.field];
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredDocuments, sortConfig, toDateValue]);

  // Handle sorting
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle document selection
  const handleSelectDocument = (documentId) => {
    const newSelected = new Set(selectedDocuments);
    if (newSelected.has(documentId)) {
      newSelected.delete(documentId);
    } else {
      newSelected.add(documentId);
    }
    setSelectedDocuments(newSelected);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedDocuments.size === sortedDocuments.length) {
      setSelectedDocuments(new Set());
    } else {
      setSelectedDocuments(new Set(sortedDocuments.map(doc => doc.id)));
    }
  };

  // Handle row expansion
  const toggleRowExpansion = (documentId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(documentId)) {
      newExpanded.delete(documentId);
    } else {
      newExpanded.add(documentId);
    }
    setExpandedRows(newExpanded);
  };

  // Get unique values for filter options
  const filterOptions = useMemo(() => {
    const types = [...new Set(processedDocuments.map(doc => doc.type).filter(Boolean))].sort();
    const entityTypes = [...new Set(processedDocuments.map(doc => doc.entityType).filter(Boolean))].sort();
    const appliesTo = [...new Set(processedDocuments.map(doc => doc.entityName).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    return { types, entityTypes, appliesTo };
  }, [processedDocuments]);

  const formatDate = useCallback((value, pattern = 'MMM dd, yyyy') => {
    const parsed = toDateValue(value);
    try {
      return parsed ? format(parsed, pattern) : '—';
    } catch (error) {
      return '—';
    }
  }, [toDateValue]);

  const formatCountdown = (daysLeft) => {
    if (typeof daysLeft !== 'number' || Number.isNaN(daysLeft)) {
      return 'No expiration';
    }
    if (daysLeft < 0) {
      return `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? '' : 's'} ago`;
    }
    if (daysLeft === 0) return 'Expires today';
    if (daysLeft === 1) return 'Expires tomorrow';
    return `${daysLeft} days left`;
  };

  // Get type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'license':
      case 'endorsement':
        return Shield;
      case 'registration':
      case 'inspection':
        return Truck;
      case 'medical':
      case 'drug_test':
        return User;
      case 'insurance':
      case 'training':
        return FileText;
      default:
        return FileText;
    }
  };

  // Quick upload handler
  const handleQuickUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadError(null);
    setUploadProgress(0);

    try {
      const uploadPromises = files.map(async (file, index) => {
        const metadata = {
          title: file.name.split('.')[0],
          type: 'general',
          category: 'documents',
          entityType: 'general',
          entityName: 'Document Library',
          uploadedBy: 'Current User',
          tags: ['quick-upload'],
          issueDate: new Date().toISOString()
        };

        return await documentStorageService.uploadDocument(
          file,
          metadata,
          {
            enableOCR: documentStorageService.canOCRFile(file),
            onProgress: (progress) => setUploadProgress(progress)
          }
        );
      });

      const results = await Promise.all(uploadPromises);
      const successful = results.filter(result => result && result.success !== false);

      if (successful.length > 0) {
        setUploadProgress(0);
        await fetchDocuments();
        // setShowQuickUpload(false); // Commented out quick upload functionality
      }
    } catch (error) {
      setUploadError('Quick upload failed: ' + error.message);
    }
  };

  // Action handlers
  const triggerFileDownload = (response, fallbackName = 'document') => {
    if (typeof window === 'undefined' || !response) return false;

    const { data, headers } = response;
    const mimeType = headers?.['content-type'] || 'application/octet-stream';
    const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
    const downloadUrl = window.URL.createObjectURL(blob);

    const disposition = headers?.['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const encodedName = filenameMatch?.[1] || filenameMatch?.[2];
    const filename = encodedName ? decodeURIComponent(encodedName) : fallbackName;

    const link = window.document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'document';
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    return true;
  };

  const handleDownloadDocument = async (document) => {
    try {
      const response = await documentStorageService.downloadDocument(document.id);
      const fallbackName = document.fileName || `${document.title || 'document'}.pdf`;
      const success = triggerFileDownload(response, fallbackName);

      if (!success) {
        setUploadError('Download failed: Unable to process file.');
      }
    } catch (error) {
      setUploadError('Download error: ' + error.message);
    }
  };

  const handleDeleteDocument = async (document) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        const result = await documentStorageService.deleteDocument(document.id, false);
        if (result?.success === false) {
          setUploadError('Delete failed: ' + (result.error || 'Unknown error'));
        } else {
          await fetchDocuments();
        }
      } catch (error) {
        setUploadError('Delete error: ' + error.message);
      }
    }
  };

  const openDocument = async (document) => {
    try {
      if (typeof window === 'undefined') return;

      if (document.fileUrl) {
        window.open(document.fileUrl, '_blank', 'noopener');
        return;
      }

      const response = await documentStorageService.getDocumentById(document.id);
      const url = response?.document?.fileUrl || response?.fileUrl;

      if (url) {
        window.open(url, '_blank', 'noopener');
      } else {
        setUploadError('Preview is not available for this document.');
      }
    } catch (error) {
      setUploadError('Unable to open document: ' + error.message);
    }
  };

  const handleAction = async (action, document) => {
    if (onDocumentAction) {
      onDocumentAction(action, document);
    }
    setUploadError(null);
    
    switch (action) {
      case 'view':
        await openDocument(document);
        break;
      case 'download':
        await handleDownloadDocument(document);
        break;
      case 'edit':
        console.log('Edit document:', document);
        break;
      case 'delete':
        await handleDeleteDocument(document);
        break;
      case 'upload_version':
        // setSelectedDocumentForVersion(document);
        // setShowVersionModal(true); // Commented out version modal functionality
        break;
      default:
        console.log(`Action: ${action}`, document);
    }
  };

  // Handle bulk actions
  const handleBulkAction = (action) => {
    const selectedDocs = sortedDocuments.filter(doc => selectedDocuments.has(doc.id));
    if (onBulkAction) {
      onBulkAction(action, selectedDocs);
    } else {
      console.log(`Bulk action: ${action}`, selectedDocs);
    }
  };

  // Render sort icon
  const renderSortIcon = (field) => {
    if (sortConfig.field !== field) {
      return <SortAsc className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ? 
      <SortAsc className="h-4 w-4 text-blue-600" /> : 
      <SortDesc className="h-4 w-4 text-blue-600" />;
  };

  // Render mobile or desktop view
  if (isMobile) {
    return (
      <div className={`min-h-screen bg-gray-50 ${className}`}>
        {/* Mobile Header */}
        <MobileDocumentHeader
          onBack={() => navigate('/compliance')}
          onUpload={() => setShowUploadModal(true)}
          onQuickUpload={() => quickUploadRef.current?.click()}
          selectedCount={selectedDocuments.size}
          totalCount={processedDocuments.length}
          onSelectAll={handleSelectAll}
          onBulkAction={handleBulkAction}
          onClearSelection={() => setSelectedDocuments(new Set())}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          documentInsights={documentInsights}
        />

        {/* Mobile Search & Filter */}
        <MobileSearchFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filters={filters}
          onFiltersChange={setFilters}
          filterOptions={filterOptions}
          documentInsights={documentInsights}
          onClearFilters={() => {
            setSearchTerm('');
            setFilters({ entityType: 'all', type: 'all', status: 'all', expiration: 'all', appliesTo: 'all' });
          }}
        />

        {/* Mobile Document List */}
        <div className="px-4 py-2">
          {isLoadingData && !hasExternalDocuments ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : dataError && !hasExternalDocuments ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">{dataError}</p>
              <button
                onClick={fetchDocuments}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Try Again
              </button>
            </div>
          ) : sortedDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || Object.values(filters).some(f => f !== 'all') 
                  ? 'No documents match your search criteria.' 
                  : 'Get started by uploading your first document.'
                }
              </p>
              {searchTerm || Object.values(filters).some(f => f !== 'all') ? (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilters({ entityType: 'all', type: 'all', status: 'all', expiration: 'all', appliesTo: 'all' });
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
                >
                  Clear all filters
                </button>
              ) : (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Upload First Document
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {sortedDocuments.map((doc) => (
                <MobileDocumentCard
                  key={doc.id}
                  document={doc}
                  isSelected={selectedDocuments.has(doc.id)}
                  onSelect={handleSelectDocument}
                  onAction={handleAction}
                  showCheckbox={selectedDocuments.size > 0 || viewMode === 'list'}
                />
              ))}
            </div>
          )}
        </div>

        {/* Hidden quick upload input */}
        <input
          ref={quickUploadRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          onChange={handleQuickUpload}
          className="hidden"
        />

        {/* Mobile Upload Modal */}
        <MobileUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUploadComplete={(results) => {
            console.log('Upload completed:', results);
            fetchDocuments();
          }}
          onError={(error) => setUploadError(error)}
        />

        {/* Mobile Error Toast */}
        {uploadError && (
          <div className="fixed bottom-4 left-4 right-4 bg-red-50 border border-red-200 rounded-lg shadow-lg p-4 z-50">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{uploadError}</p>
              </div>
              <button
                onClick={() => setUploadError(null)}
                className="ml-3 text-red-400 hover:text-red-600 flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Upload Progress Toast */}
        {uploadProgress > 0 && (
          <div className="fixed bottom-4 left-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
            <div className="flex items-center space-x-3">
              <Upload className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Uploading documents...</p>
                <div className="mt-2 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{uploadProgress}% complete</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop view (existing)
  return (
    <div className={`min-h-screen bg-gray-50 ${className}`}>
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            {/* Navigation */}
            <div className="flex items-center space-x-4 mb-6">
              <button
                onClick={() => navigate('/compliance')}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Shield className="h-4 w-4 mr-1" />
                Compliance
              </button>
              <ChevronUp className="h-4 w-4 text-gray-400 rotate-90" />
              <span className="text-sm font-medium text-gray-900">Documents</span>
            </div>

            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Document Library</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage compliance documents and track expiration dates
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* Quick Upload */}
                <div className="relative">
                  <input
                    ref={quickUploadRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleQuickUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => quickUploadRef.current?.click()}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Quick Upload
                  </button>
                </div>

                {/* Detailed Upload */}
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Document
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Insights Dashboard */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Documents */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Documents</p>
                <p className="text-2xl font-bold text-gray-900">{documentInsights.total}</p>
              </div>
            </div>
          </div>

          {/* Health Score */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className={`h-8 w-8 ${documentInsights.healthScore >= 80 ? 'text-green-600' : 
                  documentInsights.healthScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Health Score</p>
                <p className={`text-2xl font-bold ${documentInsights.healthScore >= 80 ? 'text-green-600' : 
                  documentInsights.healthScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {documentInsights.healthScore}%
                </p>
              </div>
            </div>
          </div>

          {/* Needs Attention */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Needs Attention</p>
                <p className="text-2xl font-bold text-red-600">{documentInsights.needsAttention}</p>
                <p className="text-xs text-gray-500">
                  {documentInsights.expired} expired, {documentInsights.expiringSoon} expiring
                </p>
              </div>
            </div>
          </div>

          {/* Upcoming Expirations */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Expiring Soon</p>
                <p className="text-2xl font-bold text-yellow-600">{documentInsights.expiringSoon}</p>
                <p className="text-xs text-gray-500">Next 30 days</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions for Urgent Items */}
        {documentInsights.upcomingExpirations.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-amber-800">Documents Expiring Soon</h3>
                <div className="mt-2 space-y-1">
                  {documentInsights.upcomingExpirations.slice(0, 3).map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between text-sm text-amber-700">
                      <span className="truncate">{doc.title} - {doc.entityName}</span>
                      <span className="ml-2 whitespace-nowrap font-medium">
                        {formatCountdown(doc.daysLeft)}
                      </span>
                    </div>
                  ))}
                  {documentInsights.upcomingExpirations.length > 3 && (
                    <button
                      onClick={() => setFilters(prev => ({ ...prev, expiration: 'expiring_soon' }))}
                      className="text-sm text-amber-600 hover:text-amber-800 font-medium"
                    >
                      View all {documentInsights.upcomingExpirations.length} expiring documents →
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            {/* Search Bar */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search documents by name, number, or entity..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium transition-colors ${
                    showFilters 
                      ? 'border-blue-300 text-blue-700 bg-blue-50' 
                      : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {showFilters ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                </button>

                {/* View Toggle */}
                <div className="border border-gray-300 rounded-md p-1 bg-gray-50">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                  >
                    <List className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                  >
                    <Grid className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {/* Bulk Actions */}
                {selectedDocuments.size > 0 && (
                  <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                    <span className="text-sm text-blue-700 font-medium">
                      {selectedDocuments.size} selected
                    </span>
                    <div className="h-4 border-l border-blue-300"></div>
                    <button
                      onClick={() => handleBulkAction('download')}
                      className="text-sm text-blue-700 hover:text-blue-900 font-medium"
                    >
                      Download
                    </button>
                    <button
                      onClick={() => handleBulkAction('delete')}
                      className="text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                      Delete
                    </button>
                  </div>
                )}

                <span className="text-sm text-gray-500">
                  {sortedDocuments.length} of {processedDocuments.length} documents
                </span>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
                    <select
                      value={filters.entityType}
                      onChange={(e) => setFilters(prev => ({ ...prev, entityType: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="all">All Types</option>
                      {filterOptions.entityTypes.map(entityType => (
                        <option key={entityType} value={entityType}>
                          {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                    <select
                      value={filters.type}
                      onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="all">All Types</option>
                      {filterOptions.types.map(type => (
                        <option key={type} value={type}>
                          {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="all">All Status</option>
                      <option value="valid">Valid</option>
                      <option value="expiring_soon">Expiring Soon</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expiration</label>
                    <select
                      value={filters.expiration}
                      onChange={(e) => setFilters(prev => ({ ...prev, expiration: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="all">All Periods</option>
                      <option value="expired">Expired</option>
                      <option value="expiring_soon">Expiring Soon (≤30 days)</option>
                      <option value="expiring_later">Expiring Later (31-90 days)</option>
                      <option value="valid">Valid (>90 days)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entity</label>
                    <select
                      value={filters.appliesTo}
                      onChange={(e) => setFilters(prev => ({ ...prev, appliesTo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="all">All Entities</option>
                      {filterOptions.appliesTo.map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Clear Filters */}
                {(searchTerm || Object.values(filters).some(f => f !== 'all')) && (
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilters({ entityType: 'all', type: 'all', status: 'all', expiration: 'all', appliesTo: 'all' });
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Documents List/Grid */}
        {viewMode === 'list' ? (
          /* Table View */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={handleSelectAll}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {selectedDocuments.size === sortedDocuments.length && sortedDocuments.length > 0 ? (
                          <CheckSquare className="h-4 w-4" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('title')}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>Document</span>
                        {renderSortIcon('title')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('entityName')}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>Entity</span>
                        {renderSortIcon('entityName')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('type')}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>Type</span>
                        {renderSortIcon('type')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>Status</span>
                        {renderSortIcon('status')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('expirationDate')}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>Expiration</span>
                        {renderSortIcon('expirationDate')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedDocuments.map((doc) => {
                    const TypeIcon = getTypeIcon(doc.type);
                    const StatusIcon = doc.icon || FileText;
                    
                    return (
                      <React.Fragment key={doc.id}>
                        <tr className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleSelectDocument(doc.id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              {selectedDocuments.has(doc.id) ? (
                                <CheckSquare className="h-4 w-4 text-blue-600" />
                              ) : (
                                <Square className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                  <TypeIcon className="h-5 w-5 text-gray-600" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 truncate max-w-48">
                                  {doc.title}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {doc.documentNumber || '—'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{doc.entityName}</div>
                            <div className="text-sm text-gray-500 capitalize">{doc.entityType}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 capitalize">
                              {doc.type.replace(/_/g, ' ')}
                            </div>
                            <div className="text-sm text-gray-500">{doc.category}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${doc.color}`}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {doc.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(doc.expirationDate)}
                            </div>
                            <div className={`text-sm ${typeof doc.daysLeft === 'number'
                              ? (doc.daysLeft < 0
                                ? 'text-red-600'
                                : doc.daysLeft <= 30
                                  ? 'text-amber-600'
                                  : 'text-green-600')
                              : 'text-gray-500'}`}>
                              {formatCountdown(doc.daysLeft)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleAction('view', doc)}
                                className="text-blue-600 hover:text-blue-900 transition-colors"
                                title="View Document"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleAction('download', doc)}
                                className="text-green-600 hover:text-green-900 transition-colors"
                                title="Download Document"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                              <DesktopDetailsButton 
                                document={doc} 
                                onToggle={() => toggleRowExpansion(doc.id)}
                                isExpanded={expandedRows.has(doc.id)}
                              />
                              <div className="relative">
                                <button
                                  className="text-gray-400 hover:text-gray-600 transition-colors"
                                  title="More actions"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                        {expandedRows.has(doc.id) && (
                          <tr>
                            <td colSpan="7" className="p-0">
                              <RowDetails document={doc} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedDocuments.map((doc) => {
              const TypeIcon = getTypeIcon(doc.type);
              const StatusIcon = doc.icon || FileText;
              
              return (
                <div
                  key={doc.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center">
                          <TypeIcon className="h-6 w-6 text-gray-600" />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleSelectDocument(doc.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {selectedDocuments.has(doc.id) ? (
                            <CheckSquare className="h-4 w-4 text-blue-600" />
                          ) : (
                            <Square className="h-4 w-4" />
                          )}
                        </button>
                        <button className="text-gray-400 hover:text-gray-600">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-900 truncate mb-1">
                        {doc.title}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">{doc.entityName}</p>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${doc.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {doc.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        Expires: {formatDate(doc.expirationDate, 'MMM dd')}
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleAction('view', doc)}
                          className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleAction('download', doc)}
                          className="p-1 text-green-600 hover:text-green-800 transition-colors"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <DesktopDetailsButton 
                          document={doc} 
                          onToggle={() => toggleRowExpansion(doc.id)}
                          isExpanded={expandedRows.has(doc.id)}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Expandable Details for Grid */}
                  {expandedRows.has(doc.id) && (
                    <div className="border-t border-gray-200">
                      <RowDetails document={doc} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {sortedDocuments.length === 0 && !(isLoadingData && !hasExternalDocuments) && !(dataError && !hasExternalDocuments) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || Object.values(filters).some(f => f !== 'all') 
                ? 'No documents match your search criteria.' 
                : 'Get started by uploading your first document.'
              }
            </p>
            {searchTerm || Object.values(filters).some(f => f !== 'all') ? (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilters({ entityType: 'all', type: 'all', status: 'all', expiration: 'all', appliesTo: 'all' });
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Clear all filters
              </button>
            ) : (
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Upload First Document
              </button>
            )}
          </div>
        )}

        {/* Loading State */}
        {isLoadingData && !hasExternalDocuments && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading documents...</p>
          </div>
        )}

        {/* Error State */}
        {dataError && !hasExternalDocuments && (
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-12 text-center">
            <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load documents</h3>
            <p className="text-gray-600 mb-4">{dataError}</p>
            <button
              onClick={fetchDocuments}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Try Again
            </button>
          </div>
        )}
      </div>

      {/* Upload Progress Toast */}
      {uploadProgress > 0 && (
        <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
          <div className="flex items-center space-x-3">
            <Upload className="h-5 w-5 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Uploading documents...</p>
              <div className="mt-2 w-48 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">{uploadProgress}% complete</p>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <ImprovedUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={(results) => {
          console.log('Upload completed:', results);
          fetchDocuments();
        }}
        onError={(error) => setUploadError(error)}
      />

      {/* Error Toast */}
      {uploadError && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg shadow-lg p-4 z-50 max-w-sm">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700 mt-1">{uploadError}</p>
            </div>
            <button
              onClick={() => setUploadError(null)}
              className="ml-3 text-red-400 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentListImproved;